// Shared pricing + visibility logic for the ClearBox questionnaire, used by the config
// panel, the review modal and the request-quote modal so they stay in sync.

export function shouldShowQuestion(question, answers) {
    // If the question is marked as hidden, don't show it
    if (question.hidden) {
        return false;
    }

    // Special case: Hide "lasers-to-monitor" when there's only 1 laser
    if (question.id === 'lasers-to-monitor') {
        const laserCount = answers ? answers['laser-count'] : undefined;
        if (laserCount === 1 || laserCount === '1') {
            return false;
        }
    }

    // If a parent field is required, hide until the parent is answered
    if (question.requiresParent) {
        const parentAnswer = answers ? answers[question.requiresParent] : undefined;
        if (
            parentAnswer === undefined ||
            parentAnswer === null ||
            parentAnswer === '' ||
            (Array.isArray(parentAnswer) && parentAnswer.length === 0)
        ) {
            return false;
        }
    }

    if (!question.showIf) return true;
    const { questionId, operator, value } = question.showIf;
    const answer = answers ? answers[questionId] : undefined;

    if (answer === undefined || answer === null || answer === '') {
        return false;
    }

    switch (operator) {
        case '>':
            return Number(answer) > Number(value);
        case '>=':
            return Number(answer) >= Number(value);
        case '<':
            return Number(answer) < Number(value);
        case '<=':
            return Number(answer) <= Number(value);
        case '==':
            return answer === value;
        case '!=':
            return answer !== value;
        default:
            return true;
    }
}

export function calculateMachinePriceBreakdown(machineAnswers, basePrice, additionalLaserPrice) {
    const laserCount = machineAnswers?.['laser-count'];
    const lasersToMonitor = machineAnswers?.['lasers-to-monitor'];

    if (!laserCount || laserCount === '' || laserCount <= 0) {
        return { hasBreakdown: false, additionalLasers: 0, additionalCost: 0, total: null };
    }

    if (laserCount === 1 || laserCount === '1') {
        return { hasBreakdown: false, additionalLasers: 0, additionalCost: 0, total: basePrice };
    }

    if (!lasersToMonitor || lasersToMonitor === '' || lasersToMonitor <= 0) {
        return { hasBreakdown: false, additionalLasers: 0, additionalCost: 0, total: null };
    }

    const additionalLasers = Math.max(0, parseInt(lasersToMonitor, 10) - 1);
    const additionalCost = additionalLasers * additionalLaserPrice;
    const total = basePrice + additionalCost;

    return {
        hasBreakdown: additionalLasers > 0,
        additionalLasers,
        additionalCost,
        total,
    };
}

export function calculateFleetPriceBreakdown(machines, basePrice, additionalLaserPrice) {
    const perMachine = machines.map((m) => calculateMachinePriceBreakdown(m.answers, basePrice, additionalLaserPrice));
    const machinesWithPrice = perMachine.filter((b) => b.total !== null);

    if (machinesWithPrice.length === 0) {
        return { hasBreakdown: false, additionalLasers: 0, additionalCost: 0, total: null, perMachine };
    }

    const totalAdditionalLasers = machinesWithPrice.reduce((sum, b) => sum + b.additionalLasers, 0);
    const totalAdditionalCost = machinesWithPrice.reduce((sum, b) => sum + b.additionalCost, 0);
    const total = machinesWithPrice.reduce((sum, b) => sum + b.total, 0);

    return {
        hasBreakdown: totalAdditionalLasers > 0 || machinesWithPrice.length > 1,
        additionalLasers: totalAdditionalLasers,
        additionalCost: totalAdditionalCost,
        total,
        machineCount: machinesWithPrice.length,
        perMachine,
    };
}

export function formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
}

export function getAnswerDisplayLabel(question, value) {
    if (question.type === 'single-choice' || question.type === 'single-select') {
        const option = (question.options || []).find((opt) => opt.id === value);
        return option ? option.label : String(value);
    }

    if (question.type === 'multi-choice' || question.type === 'multi-select') {
        const current = Array.isArray(value) ? value : [];
        const labels = current
            .map((id) => (question.options || []).find((opt) => opt.id === id))
            .filter(Boolean)
            .map((opt) => opt.label);
        return labels.length ? labels.join(', ') : current.join(', ');
    }

    return String(value);
}

// Visible answered questions (display-ready) plus a count of visible required questions left blank.
export function summarizeMachine(steps, answers) {
    const visibleSteps = (steps || []).filter((question) => shouldShowQuestion(question, answers));
    const answeredEntries = [];
    let missingRequiredCount = 0;

    visibleSteps.forEach((question) => {
        const value = answers ? answers[question.id] : undefined;
        const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
            if (question.required) {
                missingRequiredCount += 1;
            }
            return;
        }

        answeredEntries.push({
            id: question.id,
            title: question.title,
            displayValue: getAnswerDisplayLabel(question, value),
        });
    });

    return { answeredEntries, missingRequiredCount };
}
